import { DatePicker } from "@mui/lab";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import LocalizationProvider from "@mui/lab/LocalizationProvider";
import {
    Alert, Autocomplete, Box, CircularProgress,
    Grid,
    MenuItem,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TablePagination,
    TableRow,
    TextField,
    Typography
} from "@mui/material";
import Icon from "@mui/material/Icon";
import { withStyles } from "@mui/styles";
import axios from "axios";
import moment from "moment";
import * as React from "react";
import { LoadingWrapper } from "../../components/LoadingWrapper";
import { Config } from "../../config/config";
import { Helpers } from "../../utils/helpers";
import { PopupMessage } from "../../utils/message";
import { ACTIVITIES_FILTERS } from "./activities/list";
import { PlayerActivitiesState } from "./types/playerTypes";

interface FeedbackProps {
    playerId: string;
    activeEnv: any;
    classes: any;
}

class PlayerActivitiesMain extends React.Component<FeedbackProps, PlayerActivitiesState> {

    constructor(props) {
        super(props);

        this.state = {
            data: [],
            loaded: false,
            startDate: null,
            endDate: null,
            keys: ["ALL"],
            currentKey: "ALL",
            filters: {},
            pagination: {
                page: 0,
                perPage: 25,
                totalRecords: 0,
            },
        };
    }

    componentDidMount() {
        this.getActivities();
    }

    handleChangePage = (_event, newPage) => {
        const prevPaginationState = {...this.state.pagination};
        prevPaginationState.page = newPage || 0;

        this.setState({pagination: prevPaginationState}, () => {
            this.getActivities();
        });
    }

    handleChangeRowsPerPage = (event) => {
        const prevPaginationState = {...this.state.pagination};
        prevPaginationState.page = 1;
        prevPaginationState.perPage = parseInt(event.target.value, 10) || 1;

        this.setState({pagination: prevPaginationState}, () => {
            this.getActivities();
        });
    }

    changeKey(value) {
        this.setState({
            currentKey: value,
        }, () => {
            this.getActivities();
        });
    }

    getActivities = () => {
        this.setState({loaded: false}, async () => {
            try {
                const {page, perPage} = this.state.pagination;
                const userId = this.props.playerId;
                const response = await axios.post(`${Config.getInstance.apiUrl}/api/user/${userId}/activities/${page}/${perPage}`, {
                    start_date: this.state.startDate,
                    end_date: this.state.endDate,
                    type: this.state.currentKey,
                    filters: this.state.filters
                }, {
                    headers: {
                        Authorization: Helpers.getSessionId(),
                    },
                });

                const prevPaginationState = {...this.state.pagination};
                prevPaginationState.totalRecords = response.data?.totalRecords || 0;

                this.setState({
                    data: response.data?.data || [],
                    pagination: prevPaginationState,
                    loaded: true,
                    keys: response?.data?.keys || []
                });
            } catch (error) {
                PopupMessage.error("Couldn't fetch data!");
                this.setState({loaded: true});
            }
        });
    }

    handleDateStateChange(name: string, value) {
        this.setState({
            [name]: value || null,
        } as any, () => {
            this.getActivities();
        })
    }

    changeFilterValue(name, value, type) {
        const tempFilters = {...this.state.filters};

        tempFilters[name] = (type === "string" ? value : parseInt(value, 10));

        this.setState({
            filters: tempFilters,
        })
    }

    renderTypeFilters() {
        if (!this.state.currentKey) {
            return null;
        }

        const typeConfig = ACTIVITIES_FILTERS.find(activity => activity.type === this.state.currentKey);

        if (!typeConfig) {
            return null;
        }

        return typeConfig.filters.map(filter =>
            <Grid item xs={2} style={{ paddingRight: 10 }} key={filter.name}>
                <TextField
                    margin="normal"
                    id={filter.name}
                    label={filter.label}
                    fullWidth
                    onChange={(e) => this.changeFilterValue(filter.name, e.target.value, filter.type)}
                    onBlur={this.getActivities}
                    value={this.state.filters[filter.name] || ""}
                    style={{marginTop: 0}}
                    size="small"
                />
            </Grid>)
    }

    renderRows() {
        if (!this.state.loaded) {
            return <TableRow key="loading-row">
                <TableCell colSpan={2}>
                    {Helpers.renderSkeletonRows(3, 50)}
                </TableCell>
            </TableRow>
        }

        if ((this.state.data || []).length === 0) {
            return <TableRow key="records_not_found">
                <TableCell colSpan={2}>
                    <Alert severity="info">No activities found</Alert>
                </TableCell>
            </TableRow>
        }

        return (this.state.pagination.perPage > 0
            ? this.state.data.slice(
                this.state.pagination.page * this.state.pagination.perPage,
                this.state.pagination.page * this.state.pagination.perPage + this.state.pagination.perPage
            )
            : this.state.data
        ).map(record => (
            <TableRow key={record.id}>
                <TableCell style={{width: 45, textAlign: "center"}}>
                    <Icon color={record.icon === "warning" ? "secondary" : "primary"}>{record.icon}</Icon>
                </TableCell>
                <TableCell>
                    {record.content}
                </TableCell>
                <TableCell style={{textAlign: "right"}}>
                    {moment(record.timestamp).format("DD-MM-YYYY HH:mm:ss")}
                </TableCell>
            </TableRow>
        ))
    }

    render() {
        const {classes} = this.props;

        return (
            <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Paper className={classes.paper} elevation={0}>
                    <div className="table--header-padding">
                        <Grid container>
                            <Grid item xs={12}>
                                <Typography variant="h6" gutterBottom>
                                    Activities ({this.state.pagination.totalRecords})
                                </Typography>
                            </Grid>
                        </Grid>

                        <Grid container style={{ marginTop: 10 }}>
                            <Grid item style={{ paddingRight: 10 }}>
                                <DatePicker
                                    label="Start date"
                                    inputFormat="dd.MM.yyyy"
                                    value={this.state.startDate}
                                    onChange={(value) => this.handleDateStateChange("startDate", value)}
                                    renderInput={(params) => <TextField {...params} size="small"/>}
                                />
                            </Grid>

                            <Grid item style={{ paddingRight: 10 }}>
                                <DatePicker
                                    label="End date"
                                    inputFormat="dd.MM.yyyy"
                                    value={this.state.endDate}
                                    onChange={(value) => this.handleDateStateChange("endDate", value)}
                                    renderInput={(params) => <TextField {...params} size="small"/>}
                                />
                            </Grid>

                            <Grid item xs={2} style={{ paddingRight: 10 }}>
                                <Autocomplete
                                    onChange={(_, newValue) => this.changeKey(newValue)}
                                    value={this.state.currentKey || ""}
                                    options={this.state.keys}
                                    getOptionLabel={option => option || ""}
                                    renderOption={(props, item) => (
                                        <Box component="li" {...props}>
                                            <span style={{ marginLeft: 7, fontSize: 15 }}>{item}</span>
                                        </Box>
                                    )}
                                    renderInput={(params) =>
                                        <TextField {...params}
                                        label="Type"
                                        variant="outlined"
                                        size="small"
                                    />}
                                />
                            </Grid>

                            {this.renderTypeFilters()}
                        </Grid>
                    </div>

                    <Table>
                        <TableBody>
                            {this.renderRows()}
                        </TableBody>
                    </Table>

                    {this.state.loaded && this.state.data.length > 0 && <TablePagination
                        rowsPerPageOptions={[5, 10, 25, 50, 100]}
                        component="div"
                        count={this.state.pagination.totalRecords}
                        rowsPerPage={this.state.pagination.perPage}
                        page={this.state.pagination.page}
                        onPageChange={this.handleChangePage}
                        onRowsPerPageChange={this.handleChangeRowsPerPage}
                    />}
                </Paper>
            </LocalizationProvider>
        );
    }
}

const styles: any = theme => ({
    paper: {
        padding: 8,
    },
});

export const PlayerActivities = withStyles(styles)(PlayerActivitiesMain);
